import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Globe, Server, Palette, UserPlus, CreditCard } from 'lucide-react'
import { useAdminAuth } from "@/context/Admin"

import GeneralSettingsTab from "@/components/admin/settings/general-settings-tab"
import SystemSettingsTab from "@/components/admin/settings/system-settings-tab"
import AppearanceSettingsTab from "@/components/admin/settings/appearance-settings-tab"
import AdminAccountsTab from "@/components/admin/settings/accounts-tab"
import PaymentSettingsTab from "@/components/admin/settings/payment-settings-tab"

export default function AdminSettings() {
  const { admin } = useAdminAuth()
  const isSuperAdmin = admin?.role === "super_admin"
  
  // Restrict access to super_admin only
  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 px-2 sm:px-4 lg:px-8">
        <div className="text-center max-w-md">
          <h1 className="text-2xl sm:text-3xl font-bold text-red-600 mb-2">
            Access Denied
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            You do not have permission to access this page. Only super administrators can view and modify settings.
          </p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="flex flex-col gap-4 sm:gap-6 px-2 sm:px-4 lg:px-8">
      {/* Page Title */}
      <h1 className="text-2xl sm:text-3xl font-bold text-primary">
        Admin Settings
      </h1>
      <p className="text-sm sm:text-base text-gray-600">
        Configure global site settings, security, system preferences, and payment options.
      </p>

      <Tabs defaultValue="general" className="w-full">
        {/* Responsive Tabs List */}
        <TabsList
          className="
            flex flex-wrap sm:grid sm:grid-cols-3 lg:grid-cols-5 
            bg-gray-100 text-primary 
            gap-2 sm:gap-0 scrollbar-hide
            overflow-x-auto sm:overflow-visible
            p-2 sm:p-0
          "
        >
          <TabsTrigger value="general" className="flex items-center data-[state=active]:bg-primary data-[state=active]:text-white px-3 py-2 rounded-md whitespace-nowrap">
            <Globe className="w-4 h-4 mr-2" /> General
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center data-[state=active]:bg-primary data-[state=active]:text-white px-3 py-2 rounded-md whitespace-nowrap">
            <Server className="w-4 h-4 mr-2" /> System
          </TabsTrigger>
          <TabsTrigger value="appearance" className="flex items-center data-[state=active]:bg-primary data-[state=active]:text-white px-3 py-2 rounded-md whitespace-nowrap">
            <Palette className="w-4 h-4 mr-2" /> Appearance
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="admin-accounts" className="flex items-center data-[state=active]:bg-primary data-[state=active]:text-white px-3 py-2 rounded-md whitespace-nowrap">
              <UserPlus className="w-4 h-4 mr-2" /> Admin Accounts
            </TabsTrigger>
          )}
          <TabsTrigger value="payment" className="flex items-center data-[state=active]:bg-primary data-[state=active]:text-white px-3 py-2 rounded-md whitespace-nowrap">
            <CreditCard className="w-4 h-4 mr-2" /> Payment
          </TabsTrigger>
        </TabsList>

        {/* Tabs Content */}
        <TabsContent value="general" className="mt-4 sm:mt-6">
          <GeneralSettingsTab />
        </TabsContent>
        <TabsContent value="system" className="mt-4 sm:mt-6">
          <SystemSettingsTab />
        </TabsContent>
        <TabsContent value="appearance" className="mt-4 sm:mt-6">
          <AppearanceSettingsTab />
        </TabsContent>
        {isSuperAdmin && (
          <TabsContent value="admin-accounts" className="mt-4 sm:mt-6">
            <AdminAccountsTab />
          </TabsContent>
        )}
        <TabsContent value="payment" className="mt-4 sm:mt-6">
          <PaymentSettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
